import { useEffect, useState } from "react";

const MAX_INLINE_BYTES = 5 * 1024 * 1024;
export const PREVIEW_BYTES = 64 * 1024;

export type UrlContent = {
  loading: boolean;
  failed: boolean;
  contentType: string | undefined;
  content: string | undefined;
  size: number | undefined;
  truncated: boolean;
};

const empty: UrlContent = {
  loading: false,
  failed: false,
  contentType: undefined,
  content: undefined,
  size: undefined,
  truncated: false,
};

// CSV and SQLite have dedicated viewers that read from `url` themselves, so we
// don't read their bytes inline here even though CSV is technically text.
function isTextLike(contentType: string): boolean {
  if (contentType === "text/csv") return false;
  return (
    contentType.startsWith("text/") ||
    contentType === "application/json" ||
    contentType === "application/jsonl"
  );
}

// Text-like content is read inline (with a preview cap for very large files) so
// the renderer can syntax-highlight it. Everything else is left for the leaf
// renderer to stream from `url` directly (an <img>/<video> src, a SQLite range
// reader, a download), so we only learn its type and size here.
export function useUrlContent(url: string): UrlContent {
  const [result, setResult] = useState<UrlContent>(empty);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    // oxlint-disable-next-line react/react-compiler -- EffectSetState: resets to loading at the top of the AbortController content fetch
    setResult({ ...empty, loading: true });

    void fetch(url, { credentials: "include", signal })
      .then(async (res) => {
        if (!res.ok) {
          setResult({ ...empty, failed: true });
          return;
        }
        const contentType =
          res.headers.get("Content-Type")?.split(";")[0]?.trim() ??
          "text/plain";
        const size = Number(res.headers.get("Content-Length")) || 0;

        if (!isTextLike(contentType)) {
          res.body?.cancel();
          setResult({ ...empty, contentType, size });
          return;
        }

        const blob = await res.blob();
        if (signal.aborted) return;
        const truncated = blob.size > MAX_INLINE_BYTES;
        const content = await (
          truncated ? blob.slice(0, PREVIEW_BYTES) : blob
        ).text();
        if (signal.aborted) return;
        setResult({
          loading: false,
          failed: false,
          contentType,
          content,
          size: blob.size || size,
          truncated,
        });
      })
      .catch(() => {
        if (!signal.aborted) setResult({ ...empty, failed: true });
      });

    return () => controller.abort();
  }, [url]);

  return result;
}
